import React, { useState, useEffect, useRef, useCallback } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { IoMdArrowBack } from "react-icons/io";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../../../src/Config.js";
import Pagination from "../../../src/Component/Pagination";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

function AuditListView({ isSidebarExpanded }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const listType = searchParams.get("list_type") || "audits";
  const filterOption = searchParams.get("filter_option") || "all";
  const startDate = searchParams.get("start_date") || "";
  const endDate = searchParams.get("end_date") || "";
  const auditType = searchParams.get("audit_type") || "";
  const statusFilter = searchParams.get("status") || "";

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  const containerRef = useRef(null);

  // const formatDate = (date) => {
  //   if (!date) return "NA";
  //   try {
  //     const options = { year: "numeric", month: "2-digit", day: "2-digit" };
  //     return new Date(date)
  //       .toLocaleDateString("en-CA", options)
  //       .replace(/-/g, "/");
  //   } catch (error) {
  //     return "NA";
  //   }
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";

    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "Invalid date";

    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    // "Fri, 28 Nov 2025 12:15:37 IST"
    return `${get("weekday")}, ${get("day")} ${get("month")} ${get(
      "year",
    )} ${get("hour")}:${get("minute")}:${get("second")} IST`;
  };

  const fetchData = async (
    currentPage,
    currentItemsPerPage,
    searchQuery = "",
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      if (!authKey) {
        console.error("No authentication key found");
        setLoading(false);
        return;
      }

      const params = {
        list_type: listType,
        filter_option: filterOption || "All",
        page: currentPage,
        per_page: currentItemsPerPage,
      };

      if (auditType && auditType.trim() !== "") {
        params.audit_type = auditType;
      }

      if (statusFilter && statusFilter.trim() !== "") {
        params.status = statusFilter;
      }

      if (
        startDate &&
        endDate &&
        startDate.trim() !== "" &&
        endDate.trim() !== ""
      ) {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.query = searchQuery.trim();
      }

      console.log("Fetching Audit list with params:", params);

      const response = await axios.get(
        `${API_URL}/api/kpi/audit-management/list`,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
          params,
        },
      );

      console.log("API Response:", response.data);

      setReports(response.data.items || []);
      setTotalItems(response.data.total_items || 0);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching Audit list:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
      setLoading(false);
      setReports([]);
      setTotalItems(0);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      setPage(1);
      fetchData(1, itemsPerPage, searchQuery);
    }, 300),
    [
      itemsPerPage,
      listType,
      filterOption,
      startDate,
      endDate,
      auditType,
      statusFilter,
    ],
  );

  useEffect(() => {
    fetchData(page, itemsPerPage, searchInput);
  }, [
    page,
    itemsPerPage,
    listType,
    filterOption,
    startDate,
    endDate,
    auditType,
    statusFilter,
  ]);

  const handlePaginationChange = (currentPage, newItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(newItemsPerPage);
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (value.trim() === "") {
      setPage(1);
      fetchData(1, itemsPerPage, "");
    } else {
      debouncedSearch(value);
    }
  };

  const handleSortpopup = () => {
    setIsSortPopup(!isSortPopup);
  };

  const closePopups = () => {
    setIsSortPopup(false);
  };

  const handleRefresh = () => {
    setSearchInput("");
    setPage(1);
    fetchData(1, itemsPerPage, "");
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewClick = async (id) => {
    navigate(`/audit-view/${id}`);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "closed":
        return "#2E8B57";
      case "open":
      case "pending":
        return "#D4AC0D";
      case "in progress":
        return "#2980B9";
      case "overdue":
        return "#F81700";
      default:
        return "black";
    }
  };

  const getListTypeDisplay = () => {
    switch (listType) {
      case "audits":
        return "All Audits";
      case "open_points":
        return "Open Points";
      case "closed_points":
        return "Closed Points";
      case "all_points":
        return "All Audit Points";
      default:
        return "Audits";
    }
  };

  const formatDates = (dateString) => {
    if (!dateString || typeof dateString !== "string") return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const formatter = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      return formatter.format(date);
    } catch (error) {
      return "";
    }
  };

  // Render table based on list type
  const renderAuditHeaders = () => (
    <tr>
      <th className="center-alignment">SR. NO.</th>
      <th>REFERENCE NO</th>
      <th>AUDIT TYPE</th>
      <th>MONTH</th>
      <th>YEAR</th>
      <th className="center-alignment">TOTAL POINTS</th>
      <th className="center-alignment">CLOSED POINTS</th>
      <th className="center-alignment">OPEN POINTS</th>
      <th className="center-alignment">STATUS</th>
      <th>CREATED DATE</th>
      <th className="center-alignment">ACTION</th>
    </tr>
  );

  const renderPointHeaders = () => (
    <tr>
      <th className="center-alignment">SR. NO.</th>
      <th>AUDIT REFERENCE</th>
      <th>AUDIT TYPE</th>
      <th>POINT NO</th>
      <th>RESPONSIBLE PERSON</th>
      <th>EMAIL</th>
      <th>ORGANIZATION TYPE</th>
      {/* <th>OBSERVATION</th>
      <th>RECOMMENDATION</th> */}
      <th className="center-alignment">STATUS</th>
      <th>TARGET DATE</th>
      {listType === "closed_points" && <th>COMPLETION DATE</th>}
      {listType === "open_points" && (
        <th className="center-alignment">DAYS OVERDUE</th>
      )}
      {listType === "all_points" && <th>COMPLETION DATE</th>}
      <th className="center-alignment">ACTION</th>
    </tr>
  );

  const renderAuditRow = (report, index) => (
    <tr key={report.id} className="hoverable-row">
      <td className="center-alignment">
        {(page - 1) * itemsPerPage + index + 1}
      </td>
      <td>{report.reference_no || "NA"}</td>
      <td>{report.audit_type || "NA"}</td>
      <td>{report.month || "NA"}</td>
      <td>{report.year || "NA"}</td>
      <td className="center-alignment">{report.total_points || 0}</td>
      <td className="center-alignment">{report.closed_points || 0}</td>
      <td className="center-alignment">{report.open_points || 0}</td>
      <td className="status-schedules">
        <div
          style={{
            backgroundColor: getStatusColor(report.audit_status),
            width: "6vw",
          }}
        >
          {report.audit_status
            ? report.audit_status.charAt(0).toUpperCase() +
              report.audit_status.slice(1).toLowerCase()
            : "NA"}
        </div>
      </td>
      <td>{formatDate(report.created_at)}</td>
      <td className="center-alignment">
        <button
          onClick={() => handleViewClick(report.id)}
          className="button-bg"
        >
          View
        </button>
      </td>
    </tr>
  );

  const renderPointRow = (report, index) => (
    <tr key={report.id} className="hoverable-row">
      <td className="center-alignment">
        {(page - 1) * itemsPerPage + index + 1}
      </td>
      <td>{report.audit_reference || "NA"}</td>
      <td>{report.audit_type || "NA"}</td>
      <td>{report.point_no || "NA"}</td>
      <td>{report.responsible_person_name || "NA"}</td>
      <td>{report.person_email || "NA"}</td>
      <td>{report.organization_type || "NA"}</td>
      {/* <td>{report.observation || "NA"}</td>
      <td>{report.recommendation || "NA"}</td> */}
      <td className="status-schedules">
        <div
          style={{
            backgroundColor: getStatusColor(report.status),
            width: "6vw",
          }}
        >
          {report.status
            ? report.status.charAt(0).toUpperCase() +
              report.status.slice(1).toLowerCase()
            : "NA"}
        </div>
      </td>
      <td>{formatDate(report.target_date)}</td>
      {listType === "closed_points" && (
        <td>{formatDate(report.completion_date)}</td>
      )}
      {listType === "open_points" && (
        <td className="center-alignment">
          <span
            style={{
              color: report.days_overdue > 0 ? "#F81700" : "#2E8B57",
              fontWeight: "500",
            }}
          >
            {report.days_overdue > 0
              ? `${report.days_overdue} days`
              : "On track"}
          </span>
        </td>
      )}
      {listType === "all_points" && (
        <td>{formatDate(report.completion_date)}</td>
      )}
      <td className="center-alignment">
        <button
          onClick={() => handleViewClick(report.id)}
          className="button-bg"
        >
          View
        </button>
      </td>
    </tr>
  );

  return (
    <div
      className={`data-list-content-container page-content ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="data-list-content" ref={containerRef}>
        <div className="data-list-header">
          <div className="data-list-header-content">
            <div className="data-list-heading-content-left-side">
              {/* <Tooltip title="Back" arrow>
                <IoMdArrowBack className="back-company" onClick={handleBack} />
              </Tooltip> */}
              <span className="report-name">
                Audit Management - {getListTypeDisplay()}
              </span>

              {startDate && endDate && (
                <span
                  className="option-show"
                  style={{
                    color: "#ce9139ed",
                    marginLeft: "15px",
                    fontWeight: "500",
                  }}
                >
                  {formatDates(startDate)} - {formatDates(endDate)}
                </span>
              )}
            </div>

            <div className="data-list-heading-content-right-side">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search ..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  className="search-bar"
                />
                <IoSearchOutline className="search-bar-icon" />
              </div>

              <Tooltip title="Refresh List" arrow>
                <button className="refresh-button" onClick={handleRefresh}>
                  <TbRefresh className="refresh-icon" />
                </button>
              </Tooltip>

              <button className="sort-button" onClick={handleSortpopup}>
                <PiDotsThreeOutlineVerticalFill className="sort-icon" />
              </button>

              {isSortPopup && (
                <div className="sort-popup-container">
                  <div className="sort-popup-content">
                    <div className="sort-by">SORT BY</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="data-list-body" onClick={closePopups}>
          <div className="data-list-scroll-body">
            <div className="data-list-table">
              {loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <div>Loading...</div>
                </div>
              ) : reports.length > 0 ? (
                <table className="table multilevel-data">
                  <thead>
                    {listType === "audits"
                      ? renderAuditHeaders()
                      : renderPointHeaders()}
                  </thead>
                  <tbody className="data-list-scroll-bodys">
                    {reports.map((report, index) =>
                      listType === "audits"
                        ? renderAuditRow(report, index)
                        : renderPointRow(report, index),
                    )}
                  </tbody>
                </table>
              ) : (
                <div className="no-users-container">
                  <div className="no-users-message">
                    No {getListTypeDisplay()} Records Found
                  </div>
                </div>
              )}
            </div>

            {totalItems > 0 && (
              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuditListView;
